import { useMutation, UseMutationOptions, useQuery, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { useApiClient, ApiRequestOptions } from '@/lib/api/client';

export interface WhatsappGroupDto {
  id: string;
  whatsappChatId: string;
  displayName?: string;
  status: 'active' | 'disabled';
  activatedAt: string;
  activatedByWhatsappNumber?: string;
}

export interface WhatsappActivationCodeResponse {
  activationCode: string;
  expiresAt: string;
  status: string;
  botPhoneNumber?: string;
}

export interface WhatsappGroupsResponse {
  groups: WhatsappGroupDto[];
  botPhoneNumber: string | null;
}

const whatsappKeys = {
  all: ['whatsapp'] as const,
  groups: () => [...whatsappKeys.all, 'groups'] as const,
};

export function useWhatsappGroupsQuery(
  options?: Omit<UseQueryOptions<WhatsappGroupsResponse>, 'queryKey' | 'queryFn'>,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: whatsappKeys.groups(),
    queryFn: () => apiRequest<WhatsappGroupsResponse>(`/integrations/whatsapp/groups`),
    ...options,
  });
}

export function useWhatsappCreateActivationCodeMutation(
  options?: Omit<UseMutationOptions<WhatsappActivationCodeResponse, Error, { expiresInMinutes?: number }>, 'mutationFn'>,
  apiOptions?: ApiRequestOptions,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: { expiresInMinutes?: number }) =>
      apiRequest<WhatsappActivationCodeResponse>(
        `/integrations/whatsapp/groups/activation-codes`,
        'POST',
        data,
        undefined,
        apiOptions,
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: whatsappKeys.groups() });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useWhatsappUpdateGroupMutation(
  options?: Omit<UseMutationOptions<any, Error, { id: string; status: 'active' | 'disabled' }>, 'mutationFn'>,
  apiOptions?: ApiRequestOptions,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: { id: string; status: 'active' | 'disabled' }) =>
      apiRequest<any>(
        `/integrations/whatsapp/groups/${data.id}`,
        'PATCH',
        { status: data.status },
        undefined,
        apiOptions,
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: whatsappKeys.groups() });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

